import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import LivePreviewFrame from '@/components/LivePreviewFrame';
import { toPages, homePath, iconDataUri } from '@/lib/sitePages';

const API_URL = import.meta.env.VITE_API_URL ?? '';
const BRAND_URL = 'https://builderslab.net';

export default function WebsitePreview() {
  const { websiteId } = useParams();
  // The "your website is ready" email links here with a signed token, so the
  // owner can open their unpublished site on a device they are not signed in
  // on. Forwarded to the API, which verifies it against this website id.
  const [searchParams] = useSearchParams();
  const previewToken = searchParams.get('t');
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePath, setActivePath] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchSite() {
      try {
        // Send the auth token when logged in so owners can view unpublished sites.
        const headers = {};
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
        } catch { /* anonymous visitor */ }
        const qs = previewToken ? `?t=${encodeURIComponent(previewToken)}` : '';
        const res = await fetch(`${API_URL}/api/websites/public/${websiteId}${qs}`, { headers });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Site not found.');
        if (!cancelled) setSite(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchSite();
    return () => { cancelled = true; };
  }, [websiteId, previewToken]);

  // Update browser tab title once we have it
  useEffect(() => {
    if (site?.title) document.title = `${site.title} — Built with BuildersLab`;
    return () => { document.title = 'BuildersLab'; };
  }, [site?.title]);

  // Swap this tab's favicon to the generated mark. The site fills the whole
  // window here, so the tab belongs to it, not to BuildersLab — restored on
  // unmount so navigating back doesn't leave someone else's icon behind.
  useEffect(() => {
    const href = iconDataUri(site?.icon);
    if (!href) return;
    const link = document.querySelector('link[rel="icon"]');
    if (!link) return;
    const previous = link.getAttribute('href');
    link.setAttribute('href', href);
    return () => { if (previous) link.setAttribute('href', previous); };
  }, [site?.icon]);

  const pages = toPages(site);

  // Open on the home page once the site lands; after that the visitor's own
  // clicks inside the frame drive it.
  useEffect(() => {
    if (pages.length && !activePath) setActivePath(homePath(pages));
  }, [pages, activePath]);

  // ── Loading ──
  if (loading) {
    return (
      <div className="fixed inset-0 bg-zinc-950 flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        <p className="text-zinc-400 text-sm">Loading preview…</p>
      </div>
    );
  }

  // ── Error / Not Found ──
  if (error) {
    return (
      <div className="fixed inset-0 bg-zinc-950 flex flex-col items-center justify-center gap-5 px-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-red-400" />
        </div>
        <div>
          <h1 className="text-white font-bold text-xl mb-2">Preview Unavailable</h1>
          <p className="text-zinc-500 text-sm max-w-xs">{error}</p>
        </div>
        <a
          href={BRAND_URL}
          className="mt-2 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-400 text-ink text-sm font-semibold transition-all"
        >
           Build your own site
        </a>
      </div>
    );
  }

  // ── Preview ──
  return (
    <div className="fixed inset-0 bg-snow overflow-hidden">
      {/* Full-screen preview. Rendered through LivePreviewFrame so links between
          the generated pages navigate within the site instead of escaping the
          frame and landing the visitor back on builderslab.net. */}
      <LivePreviewFrame
        title={site.title || 'Website Preview'}
        pages={pages}
        css={site.css}
        js={site.js}
        icon={site.icon}
        activePath={activePath ?? homePath(pages)}
        onNavigate={setActivePath}
        isGenerating={false}
        className="w-full h-full border-none"
      />

      {/* BuildersLab watermark — hidden for Agency white-label.
          Deliberately quiet: it sits over someone else's design, so it reads as
          a credit line rather than an ad. Sits at 70% until hovered. */}
      {!site.remove_watermark && (
        <motion.a
          href={BRAND_URL}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ delay: 0.8, duration: 0.5, ease: 'easeOut' }}
          whileHover={{ opacity: 1 }}
          className="group fixed bottom-4 right-4 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-950/70 backdrop-blur-sm ring-1 ring-white/10 hover:ring-white/20 text-[11px] leading-none tracking-wide text-zinc-400 transition-[color,box-shadow] duration-200 select-none"
          title="Built with BuildersLab"
          aria-label="Built with BuildersLab — open builderslab.net"
        >
          Built with
          <span className="font-medium text-zinc-200 group-hover:text-orange-300/90 transition-colors duration-200">
            BuildersLab
          </span>
        </motion.a>
      )}
    </div>
  );
}
